import { useState, useCallback, useEffect, useMemo, useRef } from "react";

export interface MentionableFile {
  id: string;
  filename: string;
  doc_type?: string;
}

interface UseMentionsOptions {
  files?: MentionableFile[];
  activeFileId?: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  latex: "tex",
  markdown: "md",
  docx: "docx",
};

export function getDocTypeLabel(docType: string): string {
  return DOC_TYPE_LABELS[docType] || docType;
}

/**
 * Manages mention-dropdown state and the set of mentioned files.
 *
 * This hook is DOM-agnostic: it does NOT touch textarea or contentEditable.
 * The parent orchestrates between this hook and the input component.
 */
export function useMentions({ files, activeFileId }: UseMentionsOptions) {
  const [mentionedFileIds, setMentionedFileIds] = useState<Set<string>>(
    new Set(),
  );
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionHighlight, setMentionHighlight] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  // ─── Filtered files (dropdown candidates) ───

  const filteredFiles = useMemo(() => {
    if (!files) return [];
    const available = files.filter(
      (f) => !mentionedFileIds.has(f.id) && f.id !== activeFileId,
    );
    if (!mentionFilter) return available;
    const lower = mentionFilter.toLowerCase();
    return available.filter((f) => f.filename.toLowerCase().includes(lower));
  }, [files, mentionedFileIds, mentionFilter, activeFileId]);

  // Reset highlight when list changes
  useEffect(() => {
    setMentionHighlight(0);
  }, [filteredFiles.length, mentionFilter]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showMentionMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMentionMenu(false);
        setMentionFilter("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMentionMenu]);

  // ─── Mention tracking ───

  const addMention = useCallback((file: MentionableFile) => {
    setMentionedFileIds((prev) => new Set(prev).add(file.id));
    setShowMentionMenu(false);
    setMentionFilter("");
  }, []);

  const removeMention = useCallback((fileId: string) => {
    setMentionedFileIds((prev) => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
  }, []);

  // ─── Dropdown control ───

  /**
   * Called by the input component when @ is detected/updated or dismissed.
   * `filter === null` closes the dropdown; a string opens it with that filter.
   */
  const setMentionTrigger = useCallback(
    (filter: string | null) => {
      if (filter === null) {
        if (showMentionMenu) {
          setShowMentionMenu(false);
          setMentionFilter("");
        }
      } else {
        if (!files || files.length === 0) return;
        setShowMentionMenu(true);
        setMentionFilter(filter);
      }
    },
    [showMentionMenu, files],
  );

  const openMentionMenu = useCallback(() => {
    setShowMentionMenu(true);
    setMentionFilter("");
  }, []);

  const closeMentionMenu = useCallback(() => {
    setShowMentionMenu(false);
    setMentionFilter("");
  }, []);

  const toggleMentionMenu = useCallback(() => {
    if (showMentionMenu) closeMentionMenu();
    else openMentionMenu();
  }, [showMentionMenu, closeMentionMenu, openMentionMenu]);

  // ─── Keyboard navigation ───

  /** Returns true if the keydown was consumed by the mention system. */
  const handleMentionKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!showMentionMenu || filteredFiles.length === 0) return false;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHighlight((h) => (h + 1) % filteredFiles.length);
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHighlight(
          (h) => (h - 1 + filteredFiles.length) % filteredFiles.length,
        );
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        return true; // Caller should call selectHighlighted()
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMentionMenu();
        return true;
      }
      return false;
    },
    [showMentionMenu, filteredFiles, closeMentionMenu],
  );

  /**
   * Returns the currently highlighted file from the dropdown.
   * Call this when Enter/Tab is pressed while the dropdown is open.
   */
  const getHighlightedFile = useCallback((): MentionableFile | null => {
    if (!showMentionMenu || filteredFiles.length === 0) return null;
    return filteredFiles[mentionHighlight] ?? null;
  }, [showMentionMenu, filteredFiles, mentionHighlight]);

  // ─── Reset ───

  const resetMentions = useCallback(() => {
    setMentionedFileIds(new Set());
    setShowMentionMenu(false);
    setMentionFilter("");
  }, []);

  // ─── Derived ───

  const getMentionedFileIds = useCallback(
    (): string[] | undefined =>
      mentionedFileIds.size > 0 ? Array.from(mentionedFileIds) : undefined,
    [mentionedFileIds],
  );

  const hasFiles = !!(files && files.length > 0);

  return {
    mentionedFileIds,
    showMentionMenu,
    mentionFilter,
    mentionHighlight,
    filteredFiles,
    menuRef,
    hasFiles,
    setMentionHighlight,
    addMention,
    removeMention,
    setMentionTrigger,
    openMentionMenu,
    closeMentionMenu,
    toggleMentionMenu,
    handleMentionKeyDown,
    getHighlightedFile,
    resetMentions,
    getMentionedFileIds,
  };
}
